import React from 'react';
import { Box, InputLabel, MenuItem, Typography } from '@mui/material';
import { useLocales } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller } from 'react-hook-form';
import { markAsRequired } from '../../../../utils/formHelpers';
import FormControl from '../../FormControl';
import Select from '../../Select';
import FoldableSection from '../../FoldableSection';
import { getReverseOrderOptions } from '../../../../utils/contentService';
import { enrichTestIds } from '../../TestsIds';
import { EnrichForm } from './Enrich';

interface ReverseChronologicalProps {
  control: Control<EnrichForm>;
  isSaving: boolean;
}

export const useStyles = makeStyles()((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    margin: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'center',
    flexWrap: 'wrap',
    verticalAlign: 'middle'
  },
  selector: {
    minWidth: 360
  }
}));

export function ReverseChronological({ control, isSaving }: ReverseChronologicalProps): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const options = getReverseOrderOptions();

  return (
    <FoldableSection
      summary={<Typography variant="h6">{t('content.drawer.reverse_chronological.foldable_title')}</Typography>}
      expanded
      disableGutters
    >
      <Box className={classes.form}>
        <div className={classes.formRow}>
          <Controller
            control={control}
            rules={{ required: t('general.field_is_required') }}
            name="reverseChronologicalOrder"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormControl>
                <InputLabel id="typeSelector">
                  {markAsRequired(t('content.drawer.reverse_chronological.order'), true)}
                </InputLabel>
                <Select
                  label={markAsRequired(t('content.drawer.reverse_chronological.order'), true)}
                  className={classes.selector}
                  required
                  labelId="typeSelector"
                  value={value}
                  onChange={({ target: { value } }) => onChange(value as string)}
                  data-testid={enrichTestIds.reverseChronSelector}
                  error={!!error}
                  disabled={isSaving}
                >
                  {options.map(({ value, label }) => (
                    <MenuItem
                      key={value}
                      value={value}
                      data-testid={enrichTestIds.reverseChronSelectorItem}
                      data-treatment-id={value}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </div>
      </Box>
    </FoldableSection>
  );
}
